/**
 * fenceCopy.ts — §26 Fence the counts, the copy beside a fenced pair.
 * Pure view logic (vitest pins it; the closeFlow / forecastView pattern).
 *
 *   F-1d  "N deals" keeps its words; where the fence dropped rows the count
 *         appends "(+k excluded)" in the muted style the Forecast override
 *         column uses — never a second headline count. k comes from the
 *         WIRE (the pair's own excludedNonGbp), never from a client
 *         re-derivation; a pre-§26 wire has no k, so nothing is appended.
 *   F-1c  the Reports page renders the belt at page level (the nonGbpNote
 *         idiom, U-9's line) — before §26 the only disclosure was one click
 *         deep in a drill.
 *   F-2b  the unowned won pair is SAID beside the E360-2 line: it is in the
 *         revenue chart and in no row above.
 *   F-3   when an override stands, the Total-pipeline card points at the
 *         Forecast view and names both definitions as correct (B-11).
 */
import { money, nonGbpNote } from '../../../../lib/money'
import type { SalesFencedFigure } from '../../../../api/types'

/** "(+k excluded)" — '' when k is 0, or unknown (a pre-§26 wire). */
export function excludedSuffix(k: number | undefined): string {
  if (!k || k <= 0) return ''
  return `(+${k} excluded)`
}

/** A fenced count with its suffix: "3 open deals (+1 excluded)". */
export function fencedCount(n: number, noun: string, k: number | undefined): string {
  const head = `${n} ${noun}${n === 1 ? '' : 's'}`
  const tail = excludedSuffix(k)
  return tail ? `${head} ${tail}` : head
}

/** F-1c — the page-level belt line for Reports; '' when nothing was dropped
 *  (every live tenant today). Names what the belt IS: rows excluded from
 *  every £ figure on the page, each figure's count already net of them. */
export function reportsBeltLine(excludedNonGbp: number | undefined): string {
  const note = nonGbpNote(excludedNonGbp, 'deal')
  if (!note) return ''
  return `${note} in any £ figure on this page — each count beside a figure counts only the deals that produced it.`
}

/** F-2b — the unowned won line; '' when nothing was won by nobody this
 *  quarter (count and excluded both 0), or on a pre-§26 wire. */
export function unownedWonLine(u: SalesFencedFigure | undefined): string {
  if (!u || (u.count === 0 && u.excludedNonGbp === 0)) return ''
  const head = `${money(u.pence / 100)} across ${fencedCount(u.count, 'deal', u.excludedNonGbp)} won this quarter has no owner`
  return `${head} — it is in the revenue chart and in no row above.`
}

/** F-3 — the pointer under Total pipeline; '' when no open deal carries an
 *  override (then the two views agree and there is nothing to point at).
 *  The gap is signed as override − own: negative when the Forecast view is
 *  the smaller figure, as on Fourth Coffee today. */
export function overridePointerLine(overriddenOpenCount: number | undefined, overrideGapPence: number | undefined): string {
  if (!overriddenOpenCount || overriddenOpenCount <= 0) return ''
  const gap = overrideGapPence ?? 0
  const signed = gap < 0 ? `−${money(-gap / 100)}` : gap > 0 ? `+${money(gap / 100)}` : money(0)
  const n = `${overriddenOpenCount} override${overriddenOpenCount === 1 ? '' : 's'}`
  return `Forecast view differs by ${signed} (${n}): Total pipeline sums the deals' own value; the Forecast roll-up sums the effective amount. Both are correct.`
}
